/* eslint-disable import/prefer-default-export */
import { useEffect, useState } from 'react';
import { MAX_MOBILE_SCREEN_WIDTH } from '../../marketplace-custom-config';
import useIsMobile from '../../hooks/use-is-mobile';

// Day size values
const MOBILE_DAY_SIZE_DIVISOR = 7;
const MOBILE_DAY_SIZE_PADDING = 50;
const DESKTOP_DAY_SIZE = 56;
const DESKTOP_DAY_SIZE_PADDING = 44;
const DESKTOP_DAY_SIZE_MIN = 46;

/**
 * Custom hook to calculate appropriate day size for calendar based on screen width or container width
 * @param {Object} containerRef - Optional ref to container element to base size calculations on
 * @returns {number} - The calculated day size in pixels
 */
export const useDaySize = (containerRef = null) => {
  const { windowWidth } = useIsMobile();
  const isMobile = windowWidth < MAX_MOBILE_SCREEN_WIDTH;
  const [daySize, setDaySize] = useState(
    isMobile
      ? Math.floor((windowWidth - MOBILE_DAY_SIZE_PADDING) / MOBILE_DAY_SIZE_DIVISOR)
      : DESKTOP_DAY_SIZE_MIN
  );

  useEffect(() => {
    // If containerRef is provided, calculate based on container width and not on mobile
    if (containerRef?.current && !isMobile) {
      const calculateDaySize = () => {
        const containerWidth = containerRef.current.clientWidth;
        // Calculate based on container width (7 days per week)
        const numberOfDays = 7;
        const horizontalPadding = DESKTOP_DAY_SIZE_PADDING; // account for padding/margins
        const calculatedSize = Math.floor((containerWidth - horizontalPadding) / numberOfDays);

        const newDaySize = Math.max(DESKTOP_DAY_SIZE_MIN, calculatedSize);

        return newDaySize;
      };

      setDaySize(calculateDaySize());
    } else {
      // Default behavior based on window width
      setDaySize(
        windowWidth < MAX_MOBILE_SCREEN_WIDTH
          ? Math.floor((windowWidth - MOBILE_DAY_SIZE_PADDING) / MOBILE_DAY_SIZE_DIVISOR)
          : DESKTOP_DAY_SIZE
      );
    }

    return undefined; // Explicit return for useEffect cleanup function
  }, [containerRef, windowWidth, isMobile]);

  return daySize;
};
